'use client';

import { c as _c } from "react/compiler-runtime";
import { formatPremiumDuration, type Product } from '@shared/all/domains/products';
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import { formatUSD } from '@shared/utils/helpers/formatCurrency';
import toast from '@shared/utils/helpers/toast';
import useUser from '@shared/utils/hooks/useUser';
import { useContext, useState } from 'react';
import Button from '../common/Button';
import PurchaseModal from '../common/PurchaseModal';
import ProductImage from './ProductImage';
type Props = {
  product: Product;
};
export const ProductCard = props => {
  const $ = _c(51);
  const {
    openAuthContinueModal
  } = useContext(AuthWidgetContext);
  const {
    currentUser
  } = useUser();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  let t0;
  if ($[0] !== currentUser.isAuthenticated || $[1] !== openAuthContinueModal || $[2] !== props.product.purchasable) {
    t0 = () => {
      if (!currentUser.isAuthenticated) {
        openAuthContinueModal();
        return;
      }
      if (!props.product.purchasable) {
        toast("This product is already activated for you.", "", "error");
        return;
      }
      setButtonLoading(true);
      setPurchaseModalOpen(true);
      setButtonLoading(false);
    };
    $[0] = currentUser.isAuthenticated;
    $[1] = openAuthContinueModal;
    $[2] = props.product.purchasable;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const purchaseHandler = t0;
  const {
    displayPrice,
    tierPricingLabel,
    bulkDiscountLabel,
    statusLabel,
    price,
    type,
    purchasable,
    bestValue,
    bonusTrons
  } = props.product;
  const hasDiscount = displayPrice < price;
  const suffix = type === "MONTHLY_SUBSCRIPTION" ? "/month" : "";
  const t1 = `group relative flex flex-col justify-between rounded-xl border border-solid ${bestValue ? "border-primary" : "border-ds-border-standard"} bg-background-secondary p-6 shadow-md transition-all duration-300 hover:shadow-[0_10px_25px_-5px_rgba(var(--box-shadow-color),0.1),0_8px_10px_-6px_rgba(var(--box-shadow-color),0.1)]`;
  let t2;
  if ($[4] !== bestValue) {
    t2 = bestValue && <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase text-white shadow-md">Best Value</div>;
    $[4] = bestValue;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props.product.image || $[7] !== props.product.title || $[8] !== type) {
    t3 = props.product.image && <div className="flex shrink-0 items-center justify-center group-hover:animate-wiggle"><ProductImage imageKey={props.product.image} alt={props.product.title || "Product Image"} size="md" round={type === "TRON"} /></div>;
    $[6] = props.product.image;
    $[7] = props.product.title;
    $[8] = type;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props.product.title) {
    t4 = <h2 className="text-xl font-bold text-textPrimary">{props.product.title}</h2>;
    $[10] = props.product.title;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== bonusTrons || $[13] !== props.product.tron || $[14] !== type) {
    t5 = type === "TRON" && props.product.tron && <p className="mt-1 text-sm text-textSecondary">{(Number(props.product.tron.quantity) + bonusTrons).toLocaleString()}{" "}trons{(props.product.tron.premiumDays ?? 0) > 0 && ` · ${formatPremiumDuration(props.product.tron.premiumDays ?? 0)}`}</p>;
    $[12] = bonusTrons;
    $[13] = props.product.tron;
    $[14] = type;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== tierPricingLabel) {
    t6 = tierPricingLabel && <p className="mt-1 text-sm text-primary">{tierPricingLabel}</p>;
    $[16] = tierPricingLabel;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== bulkDiscountLabel) {
    t7 = bulkDiscountLabel && <p className="mt-1 text-sm text-emerald-500">{bulkDiscountLabel}</p>;
    $[18] = bulkDiscountLabel;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t5 || $[22] !== t6 || $[23] !== t7) {
    t8 = <div className="ml-4">{t4}{t5}{t6}{t7}</div>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t3 || $[26] !== t8) {
    t9 = <div><div className="mb-6 flex items-center">{t3}{t8}</div></div>;
    $[25] = t3;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== displayPrice || $[29] !== hasDiscount || $[30] !== price || $[31] !== purchasable || $[32] !== statusLabel || $[33] !== suffix) {
    t10 = !purchasable ? statusLabel ?? "Already Purchased" : <><span className="text-lg font-semibold">{formatUSD(displayPrice)}{suffix}</span>{hasDiscount && <span className="text-sm opacity-70"><s>{formatUSD(price)}{suffix}</s></span>}</>;
    $[28] = displayPrice;
    $[29] = hasDiscount;
    $[30] = price;
    $[31] = purchasable;
    $[32] = statusLabel;
    $[33] = suffix;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10) {
    t11 = <div className="flex items-center justify-center gap-2">{t10}</div>;
    $[35] = t10;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const t12 = !purchasable;
  let t13;
  if ($[37] !== buttonLoading || $[38] !== purchaseHandler || $[39] !== t11 || $[40] !== t12) {
    t13 = <Button titleElement={t11} onClick={purchaseHandler} loading={buttonLoading} disabled={t12} classNames="mt-4 w-full" />;
    $[37] = buttonLoading;
    $[38] = purchaseHandler;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== props.product || $[43] !== purchaseModalOpen) {
    t14 = purchaseModalOpen && <PurchaseModal product={props.product} closeHandler={() => setPurchaseModalOpen(false)} />;
    $[42] = props.product;
    $[43] = purchaseModalOpen;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t1 || $[46] !== t13 || $[47] !== t14 || $[48] !== t2 || $[49] !== t9) {
    t15 = <div data-testid="product-card" className={t1}>{t2}{t9}{t13}{t14}</div>;
    $[45] = t1;
    $[46] = t13;
    $[47] = t14;
    $[48] = t2;
    $[49] = t9;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  return t15;
};